import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaperAirplaneIcon, LockClosedIcon, XMarkIcon, DevicePhoneMobileIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const BillingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get registration data from location state
  const registrationData = location.state || {};
  if (process.env.NODE_ENV === 'development') {
    console.log('📄 BILLING PAGE LOAD - Full registration data:', registrationData);
    console.log('📄 BILLING PAGE - companyInfo:', registrationData.companyInfo);
  }
  const {
    confirmedSponsorTier,
    confirmedAdditionalSponsorships = [],
    confirmedAlaCarteItem,
    confirmedAlaCarteQuantity,
    contactInfo = {},
    companyInfo = {},
    teamName = '',
    partyName = '',
    usePrimaryAsPlayer1 = false,
    primaryPlayerNeedsGolfClubs = '',
    primaryPlayerGolfClubHandedness = '',
    players = [],
    usePrimaryAsGuest1 = false,
    dinnerGuests = [],
    specialNotes = '',
    smsUpdates = false
  } = registrationData;

  // Contact info for payment link delivery (NO PII/PCI data stored)
  const [paymentContact, setPaymentContact] = useState({
    firstName: contactInfo.firstName || '',
    lastName: contactInfo.lastName || '',
    email: contactInfo.email || '',
    phone: contactInfo.phone || '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  // Payment link delivery preferences
  const [deliveryPreferences, setDeliveryPreferences] = useState({
    sendSMS: false,
    sendEmail: false
  });

  const [isProcessingPaymentLink, setIsProcessingPaymentLink] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);

  const [agreements, setAgreements] = useState({
    termsAccepted: false,
    liabilityAccepted: false,
    marketingOptIn: false
  });

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // Load reCAPTCHA script
  useEffect(() => {
    // If no site key is configured, skip loading the script (do not block form submission)
    if (!RECAPTCHA_SITE_KEY) {
      console.warn('RECAPTCHA_SITE_KEY not configured; skipping reCAPTCHA load');
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const scriptElement = document.querySelector(`script[src*="recaptcha"]`);
      if (scriptElement) {
        document.head.removeChild(scriptElement);
      }
    };
  }, [RECAPTCHA_SITE_KEY]);

  // Execute reCAPTCHA
  const executeRecaptcha = async (): Promise<string | null> => {
    return new Promise((resolve) => {
      // If site key is not configured, resolve null quickly and allow submission to proceed
      if (!RECAPTCHA_SITE_KEY) {
        resolve(null);
        return;
      }
      if (typeof (window as any).grecaptcha === 'undefined') {
        console.warn('reCAPTCHA not loaded');
        resolve(null);
        return;
      }

      (window as any).grecaptcha.ready(() => {
        (window as any).grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' }).then((token: string) => {
          setRecaptchaToken(token);
          resolve(token);
        }).catch((error: any) => {
          console.error('reCAPTCHA execution failed:', error);
          resolve(null);
        });
      });
    });
  };

  // Scroll to top on page load with smooth animation
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  // Field validation errors
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

  const [registrationDebug, setRegistrationDebug] = useState<{
    requestPayload?: any;
    responseBody?: any;
    responseStatus?: number;
    responseOk?: boolean;
    errorMessage?: string;
    timestamp?: string;
  }>({});

  const [apiHealth, setApiHealth] = useState<{
    status?: number;
    ok?: boolean;
    body?: any;
    lastChecked?: string;
    error?: string;
  }>({});

  const [showDebugOnly, setShowDebugOnly] = useState(false);

  const additionalSponsorships = [
    { 
      id: 'beverage-cart', 
      name: 'Beverage Cart Sponsor', 
      price: 5000, 
      description: 'Mobile beverage service throughout the course',
      benefits: [
        'Logo/name displayed on all beverage carts',
        'Option for sponsored hole'
      ]
    },
    { 
      id: 'bloody-mary', 
      name: 'Bloody Mary Breakfast Sponsor', 
      price: 5000, 
      description: 'Sponsor the pre-tournament breakfast beverage station',
      benefits: [
        'Logo/name displayed at breakfast beverage station',
        'Recognition during breakfast announcements'
      ]
    },
    { 
      id: 'food-truck', 
      name: 'Food Truck Sponsor', 
      price: 5000, 
      description: 'On-course mobile food service provider',
      benefits: [
        'Logo/name displayed at the food truck',
        'Option for a table at the food truck'
      ]
    },
    { 
      id: 'air-cannon', 
      name: 'Air Cannon Sponsor', 
      price: 3000, 
      description: 'Sponsor the exciting tee shot air cannon experience',
      benefits: [
        'Logo/name displayed at the air cannon hole'
      ]
    },
    { 
      id: 'cigar-bar', 
      name: 'Cigar Bar Sponsor', 
      price: 3000, 
      description: 'Premium cigar station for participants',
      benefits: [
        'Logo/name displayed at the cigar bar'
      ]
    },
    { 
      id: 'photography', 
      name: 'Photography Sponsor', 
      price: 3000, 
      description: 'Professional event photography services',
      benefits: [
        'Logo/name displayed on the photo frames alongside Crash Champions'
      ]
    },
    { 
      id: 'raffle-prize', 
      name: 'Raffle Prize Sponsor', 
      price: 3000, 
      description: 'Provide prizes for tournament raffle drawings',
      benefits: [
        'Logo/name displayed on tables at reception'
      ]
    },
    { 
      id: 'beverage-station', 
      name: 'Beverage Station Sponsor', 
      price: 3000, 
      description: 'Fixed beverage station on the course',
      benefits: [
        'Logo/name display on bars at reception'
      ]
    },
    { 
      id: 'golf-cart', 
      name: 'Golf Cart Sponsor', 
      price: 3000, 
      description: 'Tournament golf cart fleet sponsorship',
      benefits: [
        'Logo/name displayed on golf carts'
      ]
    },
    { 
      id: 'premier-hole', 
      name: 'Premier Hole Sponsor', 
      price: 2000, 
      description: 'Sponsor a featured hole with signage and setup',
      benefits: [
        'Logo/name displayed on hole and vendor setup'
      ]
    },
    { 
      id: 'driving-range', 
      name: 'Driving Range Sponsor', 
      price: 1500, 
      description: 'Practice facility sponsorship and signage',
      benefits: [
        'Logo/name display at driving range'
      ]
    },
    { 
      id: 'closest-pin', 
      name: 'Closest to the Pin Sponsor', 
      price: 1500, 
      description: 'Sponsor the closest to pin contest',
      benefits: [
        'Logo/name displayed on hole'
      ]
    },
    { 
      id: 'longest-drive', 
      name: 'Longest Drive Sponsor', 
      price: 1500, 
      description: 'Sponsor the longest drive competition',
      benefits: [
        'Logo/name displayed on hole'
      ]
    }
  ];

  // Helper functions for à la carte display
  const getAlaCarteSummaryLabel = (alaCarteItem: any) => {
    if (!alaCarteItem) return 'À la carte Selection';
    
    switch (alaCarteItem.id) {
      case 'add-golfers':
        return 'Total Golfers';
      case 'dinner-only':
        return 'Total Dinner Guests';
      case 'silent-auction':
        return 'Total Items';
      case 'monetary-donation':
        return 'Total Monetary Funds';
      default:
        return 'À la carte Selection';
    }
  };

  const getAlaCarteCount = (alaCarteItem: any, quantityData: any) => {
    if (!alaCarteItem) return '1';
    
    switch (alaCarteItem.id) {
      case 'add-golfers':
        // Return actual player count for dynamic display
        const actualPlayers = (usePrimaryAsPlayer1 ? 1 : 0) + players.length;
        return actualPlayers.toString();
      case 'dinner-only':
        // Return actual guest count for dynamic display
        const actualGuests = (usePrimaryAsGuest1 ? 1 : 0) + dinnerGuests.length;
        return actualGuests.toString();
      case 'silent-auction':
        return '1';
      case 'monetary-donation':
        return '1';
      default:
        return '1';
    }
  };

  const TOP_TIER_SPONSOR_IDS = ['platinum', 'dinner', 'lunch'];

  const isSponsorGolferTopTierFlow = () => {
    return registrationData?.planType === 'sponsor-golfer'
      && !!confirmedSponsorTier?.id
      && TOP_TIER_SPONSOR_IDS.includes(confirmedSponsorTier.id);
  };

  const getSponsorFreeGolfers = (sponsorTier: any) => {
    if (!sponsorTier) return 0;
    switch (sponsorTier.id) {
      case 'platinum':
        return 8;
      case 'dinner':
      case 'lunch':
        return 4;
      default:
        return 0;
    }
  };

  const getGolfersPlusCount = () => {
    if (!isSponsorGolferTopTierFlow()) {
      return 0;
    }

    const totalAddedGolfers = (usePrimaryAsPlayer1 ? 1 : 0) + players.length;
    const includedGolfers = getSponsorFreeGolfers(confirmedSponsorTier);
    return Math.max(0, totalAddedGolfers - includedGolfers);
  };

  // Calculate registration summary
  const getRegistrationSummary = () => {
    if (!confirmedSponsorTier && confirmedAdditionalSponsorships.length === 0 && !confirmedAlaCarteItem) {
      return null;
    }

    const additionalSponsors = confirmedAdditionalSponsorships
      .map((id: string) => additionalSponsorships.find(s => s.id === id))
      .filter(Boolean);

    // Determine primary selection (sponsors take priority over à la carte)
    let primarySelection = null;
    let primaryCost = 0;
    let isAlaCarteSelection = false;
    
    if (confirmedSponsorTier) {
      primarySelection = confirmedSponsorTier;
      primaryCost = confirmedSponsorTier.price;
    } else if (additionalSponsors.length > 0) {
      primarySelection = additionalSponsors[0];
      primaryCost = additionalSponsors[0]?.price || 0;
    } else if (confirmedAlaCarteItem) {
      primarySelection = confirmedAlaCarteItem;
      
      // Different pricing logic based on item type  
      if (confirmedAlaCarteItem.id === 'silent-auction' || confirmedAlaCarteItem.id === 'monetary-donation') {
        // Use custom amount for Silent Auction and Monetary Donation
        const customAmount = confirmedAlaCarteItem.customAmount;
        // console.log('🔍 DEBUG - Silent/Monetary item:', confirmedAlaCarteItem);
        // console.log('🔍 DEBUG - Custom amount:', customAmount);
        // console.log('🔍 DEBUG - Custom amount type:', typeof customAmount);
        primaryCost = (customAmount !== undefined && customAmount !== null && !isNaN(Number(customAmount))) 
          ? Number(customAmount)
          : 0;
      } else if (confirmedAlaCarteItem.id === 'add-golfers') {
        // Always use the originally selected package price (e.g. 3 golfers = $450)
        primaryCost = confirmedAlaCarteQuantity?.price || 0;
      } else if (confirmedAlaCarteItem.id === 'dinner-only') {
        // Always use the originally selected package price (e.g. 4 guests = $200)
        primaryCost = confirmedAlaCarteQuantity?.price || 0;
      } else {
        // Other à la carte items use quantity-based pricing
        primaryCost = confirmedAlaCarteQuantity?.price || confirmedAlaCarteItem.price || 0;
      }
      
      // console.log('🔍 DEBUG - Final calculated primary cost:', primaryCost);
      isAlaCarteSelection = true;
    }

    const remainingSponsors = confirmedSponsorTier ? additionalSponsors : additionalSponsors.slice(1);
    
    // Add golfers to remaining sponsors if any players were added
    const totalAddedGolfers = (usePrimaryAsPlayer1 ? 1 : 0) + players.length;
    if (totalAddedGolfers > 0 && !isAlaCarteSelection) {
      // Sponsor & Golfer + top-tier sponsors: show included free golfers + paid Golfers Plus.
      if (isSponsorGolferTopTierFlow()) {
        const includedGolfers = getSponsorFreeGolfers(confirmedSponsorTier);
        const freeGolferCount = Math.min(totalAddedGolfers, includedGolfers);
        const golferPlusCount = getGolfersPlusCount();

        if (freeGolferCount > 0) {
          remainingSponsors.push({
            id: 'added-golfers-free',
            name: `Golfers (Included) (${freeGolferCount} player${freeGolferCount !== 1 ? 's' : ''})`,
            description: `${freeGolferCount} golfer spot${freeGolferCount !== 1 ? 's' : ''} included with your sponsorship`,
            price: 0,
            benefits: []
          });
        }

        if (golferPlusCount > 0) {
          remainingSponsors.push({
            id: 'added-golfers-plus',
            name: `Golfers (Plus) (${golferPlusCount} player${golferPlusCount !== 1 ? 's' : ''})`,
            description: `${golferPlusCount} additional player${golferPlusCount !== 1 ? 's' : ''} at $150 per player`,
            price: golferPlusCount * 150,
            benefits: []
          });
        }
      } else {
        // For primary sponsors: golfers are free; for additional-sponsorship-only: golfers are $150 each.
        const golferPrice = confirmedSponsorTier ? 0 : 150;
        const golfersEntry = {
          id: 'added-golfers',
          name: `Golfers (${totalAddedGolfers} players)`,
          description: `${usePrimaryAsPlayer1 ? 'Primary Contact + ' : ''}${players.length} additional player${players.length !== 1 ? 's' : ''}${golferPrice > 0 ? ' at $150 per player' : ''}`,
          price: totalAddedGolfers * golferPrice,
          benefits: []
        };
        remainingSponsors.push(golfersEntry);
      }
    }
    
    // Add dinner guests to remaining sponsors if any guests were added
    const totalDinnerGuests = (usePrimaryAsGuest1 ? 1 : 0) + dinnerGuests.length;
    if (totalDinnerGuests > 0 && !isAlaCarteSelection) {
      // Skip this entirely for à la carte selections since they're already included in primary cost
      // Dinner guests are always $50 each (both primary sponsors and additional sponsorships)
      const dinnerGuestsEntry = {
        id: 'added-dinner-guests',
        name: `Dinner Guests (${totalDinnerGuests} guest${totalDinnerGuests !== 1 ? 's' : ''})`,
        description: `${usePrimaryAsGuest1 ? 'Primary Contact + ' : ''}${dinnerGuests.length} additional dinner guest${dinnerGuests.length !== 1 ? 's' : ''} at $50 per guest`,
        price: totalDinnerGuests * 50,
        benefits: []
      };
      remainingSponsors.push(dinnerGuestsEntry);
    }
    
    // Calculate totals
    const totalSponsorsCount = (confirmedSponsorTier ? 1 : 0) + confirmedAdditionalSponsorships.length;
    const additionalCost = remainingSponsors.reduce((sum: number, sponsor: any) => sum + (sponsor?.price || 0), 0);
    const totalCost = primaryCost + additionalCost;

    return {
      totalSponsorsCount,
      primarySponsor: primarySelection,
      remainingSponsors,
      primaryCost,
      additionalCost,
      totalCost,
      isAlaCarteSelection,
      alaCarteSummaryLabel: isAlaCarteSelection ? getAlaCarteSummaryLabel(confirmedAlaCarteItem) : null,
      alaCarteCount: isAlaCarteSelection ? getAlaCarteCount(confirmedAlaCarteItem, confirmedAlaCarteQuantity) : null
    };
  };

  const summary = getRegistrationSummary();
  
  // Debug confirmedAlaCarteItem state
  // console.log('🎯 CURRENT STATE - confirmedAlaCarteItem:', confirmedAlaCarteItem);
  // console.log('🎯 CURRENT STATE - summary:', summary);

  // Phone number formatting
  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return phoneNumber;
    }
  };

  const nameRegex = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.(com|org|net)$/i;
  const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;
  const addressRegex = /^[A-Za-z0-9\s]+$/;
  const cityRegex = /^[A-Za-z\s]+$/;
  const stateRegex = /^[A-Za-z]{0,2}$/;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPaymentContact(prev => ({ ...prev, phone: formatted }));
    // Clear error when user starts typing
    if (fieldErrors.phone && formatted.trim()) {
      setFieldErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  // Build a full state payload so Back to Details restores the in-progress registration form.
  const buildBackToDetailsState = () => {
    const inferredPlanType = (registrationData as any)?.planType
      || (confirmedAlaCarteItem ? 'ala-carte' : 'sponsor-golfer');

    return {
      ...registrationData,
      planType: inferredPlanType,
      returnFromBilling: true,
      confirmedSponsorTier,
      confirmedAdditionalSponsorships,
      confirmedAlaCarteItem,
      confirmedAlaCarteQuantity,
      contactInfo: {
        ...contactInfo,
        firstName: paymentContact.firstName,
        lastName: paymentContact.lastName,
        email: paymentContact.email,
        phone: paymentContact.phone,
      },
      companyInfo,
      teamName,
      partyName,
      usePrimaryAsPlayer1,
      primaryPlayerNeedsGolfClubs,
      primaryPlayerGolfClubHandedness,
      players,
      usePrimaryAsGuest1,
      dinnerGuests,
      specialNotes,
      smsUpdates,
    };
  };

  // Scroll to field with error (updated for payment contact fields)
  const scrollToField = (fieldId: string) => {
    const fieldSelectors: {[key: string]: string} = {
      'firstName': 'input[name="firstName"]',
      'lastName': 'input[name="lastName"]', 
      'email': 'input[name="email"]',
      'phone': 'input[name="phone"]',
      'termsAccepted': 'input[id="termsAgreement"], input[id="termsAgreementMobile"]',
      'liabilityAccepted': 'input[id="liabilityWaiver"], input[id="liabilityWaiverMobile"]'
    };
    
    const selector = fieldSelectors[fieldId];
    if (selector) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
        // Focus the field after scrolling
        setTimeout(() => element.focus(), 500);
      }
    }
  };

  const checkRegistrationApiHealth = async () => {
    try {
      const healthResponse = await fetch('/api/register');
      const healthBody = await healthResponse.json().catch(() => null);

      setApiHealth({
        status: healthResponse.status,
        ok: healthResponse.ok,
        body: healthBody,
        lastChecked: new Date().toISOString(),
        error: healthResponse.ok ? undefined : healthBody?.error || `HTTP ${healthResponse.status}`,
      });
    } catch (healthErr: any) {
      setApiHealth({
        ok: false,
        error: healthErr?.message || 'Unable to reach /api/register',
        lastChecked: new Date().toISOString(),
      });
    }
  };

  const renderApiDebugOverlay = () => (
    <div className="fixed inset-0 z-50 overflow-auto bg-black/40 p-6">
      <div className="mx-auto max-w-5xl rounded-3xl border border-red-200 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-4 pb-4 border-b border-red-100 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-red-900">Registration API Debug Only</h2>
            <p className="text-sm text-red-700">Use this overlay to isolate the /api/register request and response.</p>
          </div>
          <button
            type="button"
            onClick={() => setShowDebugOnly(false)}
            className="rounded-lg border border-red-300 bg-red-100 px-4 py-2 text-sm font-semibold text-red-900 hover:bg-red-200"
          >
            Close
          </button>
        </div>
        <div className="space-y-4">
          <div className="space-y-2 text-xs font-mono text-slate-900">
            <div>
              <span className="font-semibold">Health:</span>{' '}
              {apiHealth.status ? `${apiHealth.ok ? 'OK' : `Failed (${apiHealth.status})`}` : 'Unknown'}
            </div>
            {apiHealth.error && (
              <div className="text-red-800"><span className="font-semibold">Health error:</span> {apiHealth.error}</div>
            )}
            {apiHealth.body && (
              <pre className="max-h-40 overflow-auto rounded border border-red-200 bg-slate-50 p-3">{JSON.stringify(apiHealth.body, null, 2)}</pre>
            )}
          </div>

          {registrationDebug.requestPayload && (
            <div className="rounded border border-red-200 bg-slate-50 p-3">
              <div className="font-semibold text-sm text-red-900 mb-2">Last registration request</div>
              <pre className="max-h-64 overflow-auto text-xs">{JSON.stringify(registrationDebug.requestPayload, null, 2)}</pre>
            </div>
          )}

          {registrationDebug.responseBody && (
            <div className="rounded border border-red-200 bg-slate-50 p-3">
              <div className="font-semibold text-sm text-red-900 mb-2">Last registration response</div>
              <pre className="max-h-64 overflow-auto text-xs">{JSON.stringify({ status: registrationDebug.responseStatus, ok: registrationDebug.responseOk, body: registrationDebug.responseBody }, null, 2)}</pre>
            </div>
          )}

          {registrationDebug.errorMessage && (
            <div className="rounded border border-red-300 bg-red-100 p-3 text-sm font-semibold text-red-900">
              Error: {registrationDebug.errorMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Show toast notification
  const showToastNotification = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000); // Auto hide after 5 seconds
  };

  // Validate payment contact form (minimal PII for payment link delivery)
  const validatePaymentContactForm = () => {
    const errors: {[key: string]: string} = {};
    let hasErrors = false;
    let firstErrorField: string | null = null;

    // Validate required contact fields for payment link delivery
    if (!paymentContact.firstName.trim()) {
      errors['firstName'] = 'First Name is required';
      if (!firstErrorField) firstErrorField = 'firstName';
      hasErrors = true;
    } else if (!nameRegex.test(paymentContact.firstName.trim())) {
      errors['firstName'] = 'First Name can only include letters, spaces, hyphens, or apostrophes';
      if (!firstErrorField) firstErrorField = 'firstName';
      hasErrors = true;
    } else if (paymentContact.firstName.trim().length > 20) {
      errors['firstName'] = 'First Name must be 20 characters or less';
      if (!firstErrorField) firstErrorField = 'firstName';
      hasErrors = true;
    }
    if (!paymentContact.lastName.trim()) {
      errors['lastName'] = 'Last Name is required';
      if (!firstErrorField) firstErrorField = 'lastName';
      hasErrors = true;
    } else if (!nameRegex.test(paymentContact.lastName.trim())) {
      errors['lastName'] = 'Last Name can only include letters, spaces, hyphens, or apostrophes';
      if (!firstErrorField) firstErrorField = 'lastName';
      hasErrors = true;
    }
    
    // Email or Phone required for payment link delivery
    const hasEmail = paymentContact.email.trim().length > 0;
    const hasPhone = paymentContact.phone.trim().length > 0;
    
    if (!hasEmail && !hasPhone) {
      errors['email'] = 'Email or Phone required for payment link delivery';
      errors['phone'] = 'Email or Phone required for payment link delivery';
      if (!firstErrorField) firstErrorField = 'email';
      hasErrors = true;
    }
    
    // Validate email format if provided
    if (hasEmail && !emailRegex.test(paymentContact.email.trim())) {
      errors['email'] = 'Please enter a valid email address';
      if (!firstErrorField) firstErrorField = 'email';
      hasErrors = true;
    }
    
    // Validate phone format if provided
    if (hasPhone && !phoneRegex.test(paymentContact.phone.trim())) {
      errors['phone'] = 'Phone must be in format (xxx) xxx-xxxx';
      if (!firstErrorField) firstErrorField = 'phone';
      hasErrors = true;
    }
    
    // Validate agreements
    if (!agreements.termsAccepted) {
      errors['termsAccepted'] = 'You must accept the Terms and Conditions';
      if (!firstErrorField) firstErrorField = 'termsAccepted';
      hasErrors = true;
    }
    if (!agreements.liabilityAccepted) {
      errors['liabilityAccepted'] = 'You must accept the Liability Waiver';
      if (!firstErrorField) firstErrorField = 'liabilityAccepted';
      hasErrors = true;
    }

    setFieldErrors(errors);

    if (hasErrors) {
      showToastNotification('Please complete all required fields to continue');
      // Scroll to the first error field
      if (firstErrorField) {
        setTimeout(() => {
          if (firstErrorField) {
            scrollToField(firstErrorField);
          }
        }, 100);
      }
      return false;
    }

    return true;
  };

  // Handle payment link generation and delivery
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePaymentContactForm()) {
      return;
    }

    // Execute reCAPTCHA before processing (if available). Do NOT block submission if token is unavailable.
    const token = await executeRecaptcha();
    if (!token) {
      console.warn('reCAPTCHA token not available; continuing without token');
    }
    
    setIsProcessingPaymentLink(true);
    setShowProcessingModal(true);
    
    try {
      // Save registration to database first — use the confirmation number it returns as our ID
      let registrationId = `CC2026-${Date.now().toString(36).toUpperCase()}`; // fallback only
      const selectedPlanType = String((registrationData as any)?.planType || '').toLowerCase();
      const resolvedAlaCarteType = String(confirmedAlaCarteItem?.id || '').toLowerCase();

      const resolveRegistrationType = () => {
        if (resolvedAlaCarteType) return resolvedAlaCarteType;
        if (selectedPlanType === 'sponsor-golfer') return 'sponsor-golfer';
        if (selectedPlanType === 'sponsor') return 'sponsor';
        if (selectedPlanType === 'golfer') return 'golfer';
        if (selectedPlanType === 'dinner-only' || selectedPlanType === 'silent-auction' || selectedPlanType === 'monetary-donation' || selectedPlanType === 'add-golfers') {
          return selectedPlanType;
        }
        if (confirmedSponsorTier || (confirmedAdditionalSponsorships || []).length > 0) return 'sponsor';
        return 'registration';
      };

      const persistedRegistrationType = resolveRegistrationType();
      const selectorTrace = {
        startedPlanType: selectedPlanType || null,
        selectedPlan: (registrationData as any)?.selectedPlan || null,
        confirmedAlaCarteItemId: resolvedAlaCarteType || null,
        confirmedSponsorTierId: confirmedSponsorTier?.id || null,
        additionalSponsorshipIds: confirmedAdditionalSponsorships || [],
        resolvedRegistrationType: persistedRegistrationType,
      };

      const registrationRequestPayload = {
        registrationType: persistedRegistrationType,
        planType: selectedPlanType || null,
        confirmedAlaCarteItemId: resolvedAlaCarteType || null,
        selectorTrace,
        captainFirstName: paymentContact.firstName,
        captainLastName: paymentContact.lastName,
        captainEmail: paymentContact.email,
        captainPhone: paymentContact.phone,
        captainCompany: companyInfo.companyName || '',
        totalAmount: summary?.totalCost || 0,
        teamName: teamName || '',
        sponsorshipLevel: confirmedSponsorTier?.id || null,
        additionalSponsorships: confirmedAdditionalSponsorships || [],
        teamMembers: players || [],
        usePrimaryAsPlayer1: usePrimaryAsPlayer1 || false,
        captainNeedsGolfClubs: usePrimaryAsPlayer1 && primaryPlayerNeedsGolfClubs === 'yes',
        captainGolfClubHandedness: usePrimaryAsPlayer1 && primaryPlayerNeedsGolfClubs === 'yes' ? (primaryPlayerGolfClubHandedness || null) : null,
        dinnerGuests: (dinnerGuests || []).length,
        dinnerGuestsList: dinnerGuests || [],
        usePrimaryAsGuest1: usePrimaryAsGuest1 || false,
        smsConsent: smsUpdates,
        specialRequests: specialNotes || '',
      };

      setRegistrationDebug({
        requestPayload: registrationRequestPayload,
        responseBody: null,
        responseStatus: undefined,
        responseOk: undefined,
        errorMessage: undefined,
        timestamp: new Date().toISOString(),
      });

      const regResponse = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registrationRequestPayload),
      });
      const regData = await regResponse.json().catch(() => ({}));
      const errorMessage = !regResponse.ok || !regData?.success || !regData?.data?.confirmationNumber
        ? regData?.error || 'Unable to save registration. Please try again.'
        : undefined;

      setRegistrationDebug(prev => ({
        ...prev,
        responseStatus: regResponse.status,
        responseOk: regResponse.ok,
        responseBody: regData,
        errorMessage,
      }));

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      registrationId = regData.data.confirmationNumber;
      // console.log('✅ Registration saved, ID:', registrationId);

      // If a company logo file was provided, attempt to upload it to the server's uploads directory.
      let uploadedLogoUrl: string | undefined;
      if (companyInfo?.logoFile) {
        try {
          const formData = new FormData();
          formData.append('logo', companyInfo.logoFile);
          const upl = await fetch('/api/upload-logo', {
            method: 'POST',
            body: formData
          });
          const uplJson = await upl.json().catch(() => ({}));
          if (upl.ok && uplJson?.success) {
            uploadedLogoUrl = uplJson.fileUrl || uplJson.filePath;
            console.log('✅ Logo uploaded:', uploadedLogoUrl);
          } else {
            console.warn('⚠️ Logo upload failed:', uplJson);
          }
        } catch (uplErr) {
          console.warn('⚠️ Logo upload error (continuing without logo):', uplErr);
        }
      }
      let paymentUrl: string | undefined;
      let paymentLinkWarning: string | undefined;


      // Send confirmation emails via Key Vault–secured SMTP
      // CC team ALWAYS receives email; customer email controlled by checkbox
      try {
        const emailPayload = {
          registrationId,
          sendToCustomer: deliveryPreferences.sendEmail,
          contactInfo: {
            firstName: paymentContact.firstName,
            lastName: paymentContact.lastName,
            email: paymentContact.email,
            phone: paymentContact.phone,
          },
          companyInfo: {
            companyName: companyInfo.companyName || '',
            logoUrl: companyInfo.logoUrl || ''
          },
          billingInfo: {
            firstName: paymentContact.firstName,
            lastName: paymentContact.lastName,
            email: paymentContact.email,
            phone: paymentContact.phone,
            address: '',
            city: '',
            state: '',
            zipCode: '',
          },
          primarySponsor: confirmedSponsorTier?.name || confirmedAlaCarteItem?.name || 'Registration',
          totalAmount: summary?.totalCost || 0,
          teamName: teamName || '',
          players: players || [],
          usePrimaryAsPlayer1: usePrimaryAsPlayer1 || false,
          primaryPlayerNeedsGolfClubs,
          primaryPlayerGolfClubHandedness,
          dinnerGuests: dinnerGuests || [],
          confirmedAdditionalSponsorships: confirmedAdditionalSponsorships || [],
          confirmedAlaCarteItem,
          confirmedAlaCarteQuantity,
          specialNotes: specialNotes || '',
          smsUpdates,
        };
        
        console.log('📧 EMAIL PAYLOAD - companyInfo.logoUrl:', emailPayload.companyInfo.logoUrl);
        console.log('📧 EMAIL PAYLOAD - confirmedAlaCarteItem:', emailPayload.confirmedAlaCarteItem);
        if (emailPayload.confirmedAlaCarteItem?.id === 'silent-auction') {
          console.log('📧 SILENT AUCTION - imageUrls:', emailPayload.confirmedAlaCarteItem.imageUrls);
        }
        
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailPayload),
        });
        // console.log('✅ Confirmation emails sent');
      } catch (emailError) {
        // console.warn('⚠️ Email sending failed (continuing):', emailError);
      }

      // Send short receipt confirmation based on delivery preferences
      const shortPayload = {
        registrationId,
        primarySponsor: confirmedSponsorTier?.name || confirmedAlaCarteItem?.name || 'Registration',
        additionalSponsorships: confirmedAdditionalSponsorships || [],
        players: players || [],
        dinnerGuests: dinnerGuests || [],
        totalAmount: summary?.totalCost || 0,
        primaryAmount: summary?.primaryCost || 0,
        additionalAmount: summary?.additionalCost || 0,
        usePrimaryAsPlayer1: usePrimaryAsPlayer1 || false,
        primaryPlayerNeedsGolfClubs,
        primaryPlayerGolfClubHandedness,
        usePrimaryAsGuest1: usePrimaryAsGuest1 || false,
        primaryContact: {
          firstName: paymentContact.firstName,
          lastName: paymentContact.lastName,
        },
      };

      if (deliveryPreferences.sendSMS && paymentContact.phone) {
        try {
          const smsResponse = await fetch('/api/send-sms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shortConfirmation: true,
              phone: paymentContact.phone,
              firstName: paymentContact.firstName,
              confirmationNumber: registrationId,
              ...shortPayload,
            }),
          });

          if (!smsResponse.ok) {
            const smsError = await smsResponse.json().catch(() => ({}));
            throw new Error(smsError.error || `SMS API error ${smsResponse.status}`);
          }

          // console.log('✅ Short SMS confirmation sent');
        } catch (smsErr) {
          // console.warn('⚠️ Short SMS confirmation failed, trying fallback confirmation SMS:', smsErr);

          try {
            const fallbackSmsResponse = await fetch('/api/send-sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sendConfirmation: true,
                phone: paymentContact.phone,
                firstName: paymentContact.firstName,
                registrationType: confirmedSponsorTier?.name || confirmedAlaCarteItem?.name || 'Registration',
                primarySponsor: confirmedSponsorTier?.name || confirmedAlaCarteItem?.name || 'Registration',
                totalAmount: summary?.totalCost || 0,
                confirmationNumber: registrationId,
              }),
            });

            if (!fallbackSmsResponse.ok) {
              const fallbackSmsError = await fallbackSmsResponse.json().catch(() => ({}));
              throw new Error(fallbackSmsError.error || `Fallback SMS API error ${fallbackSmsResponse.status}`);
            }

            // console.log('✅ Fallback confirmation SMS sent');
          } catch (fallbackErr) {
            // console.warn('⚠️ Fallback confirmation SMS failed (continuing):', fallbackErr);
          }
        }
      }

      if (deliveryPreferences.sendEmail) {
        try {
          await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shortReceipt: true,
              contactInfo: {
                firstName: paymentContact.firstName,
                lastName: paymentContact.lastName,
                email: paymentContact.email,
                phone: paymentContact.phone,
              },
              ...shortPayload,
            }),
          });
          // console.log('✅ Short email receipt sent');
        } catch (emailErr) {
          // console.warn('⚠️ Short email receipt failed (continuing):', emailErr);
        }
      }
      
      // Prepare data for confirmation page
      const successData = {
        registrationId,
        primarySponsor: confirmedSponsorTier?.name || confirmedAlaCarteItem?.name || 'Registration',
        contactInfo: {
          firstName: paymentContact.firstName,
          lastName: paymentContact.lastName,
          email: paymentContact.email,
          phone: paymentContact.phone
        },
        companyInfo: {
          companyName: companyInfo.companyName || 'Event Registration',
          // Prefer uploaded logo URL/path if upload succeeded; fall back to a local object URL when available
          companyLogo: uploadedLogoUrl || (companyInfo.logoFile ? URL.createObjectURL(companyInfo.logoFile) : undefined)
        },
        billingInfo: {
          firstName: paymentContact.firstName,
          lastName: paymentContact.lastName,
          email: paymentContact.email,
          phone: paymentContact.phone,
          address: paymentContact.address || '',
          city: paymentContact.city || '',
          state: paymentContact.state || '',
          zipCode: paymentContact.zip || ''
        },
        totalAmount: summary?.totalCost || 0,
        paymentUrl,
        paymentLinkWarning,
        teamName,
        players: players || [],
        usePrimaryAsPlayer1: usePrimaryAsPlayer1 || false,
        primaryPlayerNeedsGolfClubs,
        primaryPlayerGolfClubHandedness,
        dinnerGuests: dinnerGuests || [],
        usePrimaryAsGuest1: usePrimaryAsGuest1 || false,
        specialNotes,
        confirmedAdditionalSponsorships: confirmedAdditionalSponsorships || [],
        confirmedAlaCarteItem,
        confirmedAlaCarteQuantity,
        smsUpdates: deliveryPreferences.sendSMS,
        paymentSecure: !!paymentUrl
      };

      setToastMessage(paymentUrl
        ? '✅ Registration saved! Redirecting to confirmation...'
        : '✅ Registration saved. Payment link is temporarily unavailable; you can retry from confirmation.');
      
      setTimeout(() => {
        setIsProcessingPaymentLink(false);
        setShowToast(false);
        navigate('/registration-success', { state: successData });
      }, 1500);
      
    } catch (error) {
      // console.error('❌ Registration failed:', error);
      setIsProcessingPaymentLink(false);
      setShowProcessingModal(false);
      setToastMessage(`Registration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowToast(true);
      setTimeout(() => { setShowToast(false); }, 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Animation Styles */}
      <style>{`
        .animate-slide-in {
          animation: slideIn 0.3s ease-out forwards;
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      
      <Navigation />
      
      {/* Processing Modal */}
      {showProcessingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm" />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl px-10 py-10 max-w-sm w-full mx-4 text-center">
            {/* Spinner */}
            <div className="flex justify-center mb-6">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 rounded-full border-4 border-gray-200" />
                <div className="absolute inset-0 rounded-full border-4 border-red-600 border-t-transparent animate-spin" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Your Registration</h2>
            <p className="text-gray-500 text-sm">Please wait while we securely submit your registration&hellip;</p>
            
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-24 right-4 z-50 animate-slide-in">
          <div className="bg-gray-800 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 mr-3">
                <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                  <XMarkIcon className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{toastMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tournament Header */}
      <div className="bg-white shadow-sm border-b mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {/* Tournament Logo */}
              <div className="mr-6">
                <img 
                  src={`${process.env.PUBLIC_URL}/assets/ChampionsClassicLogo-Final-Black.png`} 
                  alt="Champions Classic 2026" 
                  className="object-cover rounded-lg"
                  style={{ height: '110px', width: '110px' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Champions Classic 2026</h1>
                <p className="text-lg text-gray-600 mt-2">
                  Thursday, September 10, 2026 @ 8:00am
                </p>
                <p className="text-sm text-red-600 font-medium mt-1">
                  Secure Payment & Billing
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Billing Form */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* 1. Payment Contact Information (NO PII/PCI Data Stored) */}
            <div className="bg-white rounded-xl shadow-sm border p-8">
              <div className="flex items-center mb-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mr-4">
                  <PaperAirplaneIcon className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Billing and Payment Link</h2>
              </div>
              
              {/* Security Notice */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <LockClosedIcon className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-800">PCI/PII Compliant</p>
                    <p className="text-xs text-green-700">No sensitive payment data collected or stored on this system</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={paymentContact.firstName}
                      onChange={(e) => {
                        setPaymentContact(prev => ({ ...prev, firstName: e.target.value }));
                        if (fieldErrors.firstName && e.target.value.trim()) {
                          setFieldErrors(prev => ({ ...prev, firstName: '' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter first name"
                    />
                    {fieldErrors.firstName && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.firstName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={paymentContact.lastName}
                      onChange={(e) => {
                        setPaymentContact(prev => ({ ...prev, lastName: e.target.value }));
                        if (fieldErrors.lastName && e.target.value.trim()) {
                          setFieldErrors(prev => ({ ...prev, lastName: '' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter last name"
                    />
                    {fieldErrors.lastName && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.lastName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={paymentContact.email}
                      onChange={(e) => {
                        setPaymentContact(prev => ({ ...prev, email: e.target.value }));
                        if (fieldErrors.email && e.target.value.trim()) {
                          setFieldErrors(prev => ({ ...prev, email: '' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter email for payment link"
                    />
                    {fieldErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={paymentContact.phone}
                      onChange={handlePhoneChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="(555) 123-4567"
                    />
                    {fieldErrors.phone && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.phone}</p>
                    )}
                  </div>

                  {/* Billing Address - full width */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Address
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={paymentContact.address}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || addressRegex.test(value)) {
                          setPaymentContact(prev => ({ ...prev, address: value }));
                          if (fieldErrors.address) {
                            setFieldErrors(prev => ({ ...prev, address: '' }));
                          }
                        } else {
                          setFieldErrors(prev => ({ ...prev, address: 'Address can only contain letters, numbers, and spaces' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="123 Main St"
                    />
                    {fieldErrors.address && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.address}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={paymentContact.city}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || cityRegex.test(value)) {
                          setPaymentContact(prev => ({ ...prev, city: value }));
                          if (fieldErrors.city) {
                            setFieldErrors(prev => ({ ...prev, city: '' }));
                          }
                        } else {
                          setFieldErrors(prev => ({ ...prev, city: 'City can only contain letters and spaces' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="City"
                    />
                    {fieldErrors.city && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.city}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={paymentContact.state}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          if (stateRegex.test(value)) {
                            setPaymentContact(prev => ({ ...prev, state: value }));
                            if (fieldErrors.state) {
                              setFieldErrors(prev => ({ ...prev, state: '' }));
                            }
                          } else {
                            setFieldErrors(prev => ({ ...prev, state: 'State must be 2 letters only' }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="TX"
                        maxLength={2}
                      />
                      {fieldErrors.state && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.state}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zip Code
                      </label>
                      <input
                        type="text"
                        name="zip"
                        value={paymentContact.zip}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numericValue = value.replace(/\D/g, '').slice(0, 5);
                          if (value !== numericValue && value !== '') {
                            setFieldErrors(prev => ({ ...prev, zip: 'Zip code must be 5 digits only' }));
                          } else {
                            if (fieldErrors.zip) {
                              setFieldErrors(prev => ({ ...prev, zip: '' }));
                            }
                          }
                          setPaymentContact(prev => ({ ...prev, zip: numericValue }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="12345"
                        maxLength={5}
                      />
                      {fieldErrors.zip && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors.zip}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Delivery Method Selection */}
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Receipt of Payment Delivery</h4>
                  <p className="text-sm text-gray-600 mb-4">Choose how you'd like to receive your receipt of payment:</p>
                  
                  <div className="space-y-4">
                    {/* SMS Option */}
                    <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="smsDelivery"
                        checked={deliveryPreferences.sendSMS}
                        onChange={(e) => setDeliveryPreferences(prev => ({ ...prev, sendSMS: e.target.checked }))}
                        className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center">
                          <DevicePhoneMobileIcon className="h-5 w-5 text-gray-500 mr-2" />
                          <label htmlFor="smsDelivery" className="text-sm font-medium text-gray-900">
                            Send me SMS updates about my registration (optional)
                          </label>
                        </div>
                        <p className="text-xs text-gray-900 mt-1">
                          *A receipt copy of your confirmation and registration amount sent to your phone.
                        </p>
                      </div>
                    </div>

                    {/* Email Option */}
                    <div className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="emailDelivery"
                        checked={deliveryPreferences.sendEmail}
                        onChange={(e) => setDeliveryPreferences(prev => ({ ...prev, sendEmail: e.target.checked }))}
                        className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center">
                          <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-2" />
                          <label htmlFor="emailDelivery" className="text-sm font-medium text-gray-900">
                            Send via Email
                          </label>
                        </div>
                        <p className="text-xs text-gray-900 mt-1">
                          *Detailed registration and payment receipt sent to your email address.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {fieldErrors.delivery && (
                    <p className="mt-2 text-sm text-red-600">{fieldErrors.delivery}</p>
                  )}
                </div>
              </form>
            </div>

            {/* 2. Secure Payment Link Information */}
            <div className="bg-black border border-gray-300 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#f1f1f1] mr-3">
                  <LockClosedIcon className="h-4 w-4 text-black" />
                </div>
                <h3 className="text-lg font-semibold text-white">What To Expect After Submitting Registration</h3>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-white">
                  <div className="flex items-center">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold mr-2">1</span>
                    Submit Registration to save your spot
                  </div>
                  <div className="flex items-center">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold mr-2">2</span>
                    Click 'Continue to Payment' on the confirmation page
                  </div>
                  <div className="flex items-center">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold mr-2">3</span>
                    Complete payment securely through Xplor Pay
                  </div>
                </div>
                
                <p className="text-xs text-white italic mt-4">
                  Payment processed securely through Clearent/Xplor Pay - No payment data stored on our servers
                </p>
              </div>
            </div>

            {/* Desktop - 3. Agreement Checkboxes */}
            <div className="hidden lg:block bg-white rounded-xl shadow-sm border p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Agreements & Consent</h3>
              
              <div className="space-y-6">
                {/* Terms and Conditions */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="termsAgreement"
                    checked={agreements.termsAccepted}
                    onChange={(e) => {
                      setAgreements(prev => ({ ...prev, termsAccepted: e.target.checked }));
                      // Clear error when user accepts terms
                      if (fieldErrors.termsAccepted && e.target.checked) {
                        setFieldErrors(prev => ({ ...prev, termsAccepted: '' }));
                      }
                    }}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                  />
                  <div>
                    <label htmlFor="termsAgreement" className="text-sm text-gray-700">
                      I agree to the{' '}
                      <a href="/terms" className="text-red-600 hover:text-red-800 underline">
                        Terms and Conditions
                      </a>
                      {' '}*
                    </label>
                    {fieldErrors.termsAccepted && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.termsAccepted}</p>
                    )}
                  </div>
                </div>

                {/* Liability Waiver */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="liabilityWaiver"
                    checked={agreements.liabilityAccepted}
                    onChange={(e) => {
                      setAgreements(prev => ({ ...prev, liabilityAccepted: e.target.checked }));
                      // Clear error when user accepts liability waiver
                      if (fieldErrors.liabilityAccepted && e.target.checked) {
                        setFieldErrors(prev => ({ ...prev, liabilityAccepted: '' }));
                      }
                    }}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                  />
                  <div>
                    <label htmlFor="liabilityWaiver" className="text-sm text-gray-700">
                      I agree to the{' '}
                      <a href="/liability-waivers" className="text-red-600 hover:text-red-800 underline">
                        Liability Waivers
                      </a>
                      {' '}and authorize emergency medical treatment if necessary *
                    </label>
                    {fieldErrors.liabilityAccepted && (
                      <p className="mt-1 text-sm text-red-600">{fieldErrors.liabilityAccepted}</p>
                    )}
                  </div>
                </div>

                {/* Marketing Updates (Optional) */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="marketingUpdates"
                    checked={agreements.marketingOptIn}
                    onChange={(e) => setAgreements(prev => ({ ...prev, marketingOptIn: e.target.checked }))}
                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                  />
                  <label htmlFor="marketingUpdates" className="text-sm text-gray-700">
                    I'd like to receive updates about future tournaments and events
                  </label>
                </div>

                {/* reCAPTCHA Notice */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-800 underline">
                      Privacy Policy
                    </a>{' '}and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-800 underline">
                      Terms of Service
                    </a>{' '}apply.
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop - 4. Action Buttons */}
            <div className="hidden lg:block bg-white rounded-xl shadow-sm border p-8">
              <div className="flex justify-between items-center">
                {/* Back to Details Button */}
                <button
                  type="button"
                  onClick={() => navigate('/register', {
                    state: buildBackToDetailsState()
                  })}
                  className="px-8 py-3 text-lg font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  ← Back to Details
                </button>

                {/* Send Payment Link Button */}
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={!agreements.termsAccepted || !agreements.liabilityAccepted || isProcessingPaymentLink}
                  className={`px-12 py-4 text-lg font-semibold rounded-xl transition-all duration-200 ${
                    (agreements.termsAccepted && agreements.liabilityAccepted && !isProcessingPaymentLink)
                      ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isProcessingPaymentLink ? (
                    <span className="flex items-center">
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                      Submit Registration
                    </span>
                  )}
                </button>
              </div>
            </div>

          </div>

          {/* Mobile Registration Summary - Shows on mobile, hidden on desktop */}
          <div className="lg:hidden bg-white rounded-xl shadow-sm border p-6 order-3">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Registration Summary</h3>
            
            {summary ? (
              <div className="space-y-4">
                {/* Total Selections (Sponsorships or À la carte) */}
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-600">
                    {summary.isAlaCarteSelection ? summary.alaCarteSummaryLabel : 'Total Sponsorships'}
                  </span>
                  <span className="text-gray-900 font-medium">
                    {summary.isAlaCarteSelection ? summary.alaCarteCount : summary.totalSponsorsCount}
                  </span>
                </div>

                {/* Primary Selection (Sponsor or À la carte) */}
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">
                    {summary.isAlaCarteSelection && confirmedAlaCarteQuantity 
                      ? `${summary.primarySponsor?.name} - ${confirmedAlaCarteQuantity.label}`
                      : summary.primarySponsor?.name}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {summary.isAlaCarteSelection && confirmedAlaCarteItem?.id === 'silent-auction' 
                      ? 'Auctioning items for Champions'
                      : summary.primarySponsor?.description}
                  </p>
                  {/* Per-unit pricing for à la carte items */}
                  {summary.isAlaCarteSelection && confirmedAlaCarteItem && (
                    <p className="text-xs text-gray-400">
                      {confirmedAlaCarteItem.id === 'add-golfers' && 'x $150 per player'}
                      {confirmedAlaCarteItem.id === 'dinner-only' && 'x $50 per guest'}
                      {confirmedAlaCarteItem.id === 'silent-auction' && `Auction Item Value: $${((confirmedAlaCarteQuantity?.price || confirmedAlaCarteQuantity?.customAmount || confirmedAlaCarteItem.customAmount) || 0).toLocaleString()}`}
                      {confirmedAlaCarteItem.id === 'monetary-donation' && `Donation Amount: $${((confirmedAlaCarteQuantity?.price || confirmedAlaCarteQuantity?.customAmount || confirmedAlaCarteItem.customAmount) || 0).toLocaleString()}`}
                    </p>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      {!summary.isAlaCarteSelection && confirmedSponsorTier && summary.primarySponsor?.id === confirmedSponsorTier.id && (
                        <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                          {summary.primarySponsor?.id === 'platinum' ? 'Two foursomes included' : 'One foursome included'}
                          <br />Saves ${summary.primarySponsor?.savings?.toLocaleString()}
                        </div>
                      )}
                    </span>
                    <span className="text-xl font-bold text-gray-900">${summary.primaryCost.toLocaleString()}</span>
                  </div>
                </div>

                {/* Additional Sponsorships Section */}
                {summary.remainingSponsors.length > 0 && (
                  <>
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-3">Additional Sponsorships/Event Options</h4>
                      <div className="space-y-3">
                        {summary.remainingSponsors.map((sponsor: any) => (
                          <div key={sponsor?.id} className="flex justify-between items-center">
                            <div>
                              <span className="text-sm font-medium text-gray-900">{sponsor?.name}</span>
                              <p className="text-xs text-gray-500">{sponsor?.description}</p>
                            </div>
                            <span className="text-sm font-bold text-gray-900">${sponsor?.price.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Total */}
                <div className="pt-4 border-t-2 border-gray-300">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-red-600">${summary.totalCost.toLocaleString()}</span>
                  </div>
                </div>

                {/* What's Included Section */}
                {summary.primarySponsor && (
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-3">What's Included:</h4>
                      <ul className="space-y-1 text-sm text-gray-700">
                        {summary.primarySponsor.benefits?.map((benefit: string, index: number) => (
                          <li key={index} className="flex items-start">
                            <span className="text-red-600 mr-2">•</span>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* DEBUG SECTION HIDDEN
                <div className="hidden mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                    <div>
                      <h4 className="text-base font-semibold">Registration API Debug</h4>
                      <p className="text-xs text-red-700">Visible request/response details for /api/register.</p>
                    </div>
                    {process.env.NODE_ENV === 'development' && (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={checkRegistrationApiHealth}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 font-semibold hover:bg-red-100"
                        >
                          Check API
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDebugOnly(true)}
                          className="inline-flex items-center px-3 py-1.5 rounded-lg border border-red-300 bg-red-100 text-red-900 font-semibold hover:bg-red-200"
                        >
                          Debug only mode
                        </button>
                      </div>
                    )}
                  </div>

                  {process.env.NODE_ENV === 'development' && (
                    <div className="space-y-2 text-xs font-mono">
                      <div>
                        <span className="font-semibold">Health:</span>{' '}
                        {apiHealth.status ? `${apiHealth.ok ? 'OK' : `Failed (${apiHealth.status})`}` : 'Unknown'}
                      </div>
                      {apiHealth.error && (
                        <div className="text-red-800"><span className="font-semibold">Health error:</span> {apiHealth.error}</div>
                      )}
                      {apiHealth.body && (
                        <pre className="max-h-36 overflow-auto rounded border border-red-200 bg-white p-3 text-xs text-slate-900">{JSON.stringify(apiHealth.body, null, 2)}</pre>
                      )}
                    </div>
                  )}

                  {process.env.NODE_ENV === 'development' && registrationDebug.requestPayload && (
                    <div className="mt-4 rounded border border-red-200 bg-white p-3">
                      <div className="font-semibold text-sm text-red-900 mb-2">Last registration request</div>
                      <pre className="max-h-48 overflow-auto text-xs text-slate-900">{JSON.stringify(registrationDebug.requestPayload, null, 2)}</pre>
                    </div>
                  )}

                  {registrationDebug.responseBody && (
                    <div className="mt-4 rounded border border-red-200 bg-white p-3">
                      <div className="font-semibold text-sm text-red-900 mb-2">Last registration response</div>
                      <pre className="max-h-48 overflow-auto text-xs text-slate-900">{JSON.stringify({ status: registrationDebug.responseStatus, ok: registrationDebug.responseOk, body: registrationDebug.responseBody }, null, 2)}</pre>
                    </div>
                  )}

                  {registrationDebug.errorMessage && (
                    <div className="mt-4 rounded border border-red-300 bg-red-100 p-3 text-sm font-semibold text-red-900">
                      Error: {registrationDebug.errorMessage}
                    </div>
                  )}
                </div>
                */}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No registration data found</p>
              </div>
            )}
          </div>

          {/* Mobile Agreements & Consent - Shows on mobile */}
          <div className="lg:hidden bg-white rounded-xl shadow-sm border p-8 order-4">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Agreements & Consent</h3>
            
            <div className="space-y-6">
              {/* Terms and Conditions */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="termsAgreementMobile"
                  checked={agreements.termsAccepted}
                  onChange={(e) => {
                    setAgreements(prev => ({ ...prev, termsAccepted: e.target.checked }));
                    // Clear error when user accepts terms
                    if (fieldErrors.termsAccepted && e.target.checked) {
                      setFieldErrors(prev => ({ ...prev, termsAccepted: '' }));
                    }
                  }}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                />
                <div>
                  <label htmlFor="termsAgreementMobile" className="text-sm text-gray-700">
                    I agree to the{' '}
                    <a href="/terms" className="text-red-600 hover:text-red-800 underline">
                      Terms and Conditions
                    </a>
                    {' '}*
                  </label>
                  {fieldErrors.termsAccepted && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.termsAccepted}</p>
                  )}
                </div>
              </div>

              {/* Liability Waiver */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="liabilityWaiverMobile"
                  checked={agreements.liabilityAccepted}
                  onChange={(e) => {
                    setAgreements(prev => ({ ...prev, liabilityAccepted: e.target.checked }));
                    // Clear error when user accepts liability waiver
                    if (fieldErrors.liabilityAccepted && e.target.checked) {
                      setFieldErrors(prev => ({ ...prev, liabilityAccepted: '' }));
                    }
                  }}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                />
                <div>
                  <label htmlFor="liabilityWaiverMobile" className="text-sm text-gray-700">
                    I agree to the{' '}
                    <a href="/liability-waivers" className="text-red-600 hover:text-red-800 underline">
                      Liability Waivers
                    </a>
                    {' '}and authorize emergency medical treatment if necessary *
                  </label>
                  {fieldErrors.liabilityAccepted && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.liabilityAccepted}</p>
                  )}
                </div>
              </div>

              {/* Marketing Updates (Optional) */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="marketingUpdatesMobile"
                  checked={agreements.marketingOptIn}
                  onChange={(e) => setAgreements(prev => ({ ...prev, marketingOptIn: e.target.checked }))}
                  className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded accent-red-600"
                />
                <label htmlFor="marketingUpdatesMobile" className="text-sm text-gray-700">
                  I'd like to receive updates about future tournaments and events
                </label>
              </div>
            </div>
          </div>

          {/* Mobile Action Buttons - Stacked full width */}
          <div className="lg:hidden bg-white rounded-xl shadow-sm border p-8 order-5">
            <div className="space-y-4">
              {/* Back to Details Button */}
              <button
                type="button"
                onClick={() => navigate('/register', {
                  state: buildBackToDetailsState()
                })}
                className="w-full px-8 py-4 text-lg font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                ← Back to Details
              </button>

              {/* Send Payment Link Button */}
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={!agreements.termsAccepted || !agreements.liabilityAccepted || isProcessingPaymentLink}
                className={`w-full px-12 py-4 text-lg font-semibold rounded-xl transition-all duration-200 ${
                  (agreements.termsAccepted && agreements.liabilityAccepted && !isProcessingPaymentLink)
                    ? 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isProcessingPaymentLink ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                    Submit Registration
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Desktop Registration Summary Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Registration Summary</h3>
              
              {summary ? (
                <div className="space-y-4">
                  {/* Total Selections (Sponsorships or À la carte) */}
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">
                      {summary.isAlaCarteSelection ? summary.alaCarteSummaryLabel : 'Total Sponsorships'}
                    </span>
                    <span className="text-gray-900 font-medium">
                      {summary.isAlaCarteSelection ? summary.alaCarteCount : summary.totalSponsorsCount}
                    </span>
                  </div>

                  {/* Primary Selection (Sponsor or À la carte) */}
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900">
                      {summary.isAlaCarteSelection && confirmedAlaCarteQuantity 
                        ? `${summary.primarySponsor?.name} - ${confirmedAlaCarteQuantity.label}`
                        : summary.primarySponsor?.name}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {summary.isAlaCarteSelection && confirmedAlaCarteItem?.id === 'silent-auction' 
                        ? 'Auctioning items for Champions'
                        : summary.primarySponsor?.description}
                    </p>
                    {/* Per-unit pricing for à la carte items */}
                    {summary.isAlaCarteSelection && confirmedAlaCarteItem && (
                      <p className="text-xs text-gray-400">
                        {confirmedAlaCarteItem.id === 'add-golfers' && 'x $150 per player'}
                        {confirmedAlaCarteItem.id === 'dinner-only' && 'x $50 per guest'}
                        {confirmedAlaCarteItem.id === 'silent-auction' && `Auction Item Value: $${((confirmedAlaCarteQuantity?.price || confirmedAlaCarteQuantity?.customAmount || confirmedAlaCarteItem.customAmount) || 0).toLocaleString()}`}
                        {confirmedAlaCarteItem.id === 'monetary-donation' && `Donation Amount: $${((confirmedAlaCarteQuantity?.price || confirmedAlaCarteQuantity?.customAmount || confirmedAlaCarteItem.customAmount) || 0).toLocaleString()}`}
                      </p>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        {!summary.isAlaCarteSelection && confirmedSponsorTier && summary.primarySponsor?.id === confirmedSponsorTier.id && (
                          <div className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            {summary.primarySponsor?.id === 'platinum' ? 'Two foursomes included' : 'One foursome included'}
                            <br />Saves ${summary.primarySponsor?.savings?.toLocaleString()}
                          </div>
                        )}
                      </span>
                      <span className="text-xl font-bold text-gray-900">${summary.primaryCost.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Additional Sponsorships Section */}
                  {summary.remainingSponsors.length > 0 && (
                    <>
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-3">Additional Sponsorships/Event Options</h4>
                        <div className="space-y-3">
                          {summary.remainingSponsors.map((sponsor: any) => (
                            <div key={sponsor?.id} className="flex justify-between items-center">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{sponsor?.name}</span>
                                <p className="text-xs text-gray-500">{sponsor?.description}</p>
                              </div>
                              <span className="text-sm font-bold text-gray-900">${sponsor?.price.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Total */}
                  <div className="pt-4 border-t-2 border-gray-300">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total</span>
                      <span className="text-xl font-bold text-red-600">${summary.totalCost.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* What's Included Section */}
                  {summary.primarySponsor && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">What's Included:</h4>
                        <ul className="space-y-1 text-sm text-gray-700">
                          {summary.primarySponsor.benefits?.map((benefit: string, index: number) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-600 mr-2">•</span>
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* DEBUG SECTION HIDDEN - DESKTOP SIDEBAR
                  <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3">
                      <div>
                        <h4 className="text-base font-semibold">Registration API Debug</h4>
                        <p className="text-xs text-red-700">Visible request/response details for /api/register.</p>
                      </div>
                      {process.env.NODE_ENV === 'development' && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={checkRegistrationApiHealth}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg border border-red-300 bg-white text-red-700 font-semibold hover:bg-red-100"
                          >
                            Check API
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowDebugOnly(true)}
                            className="inline-flex items-center px-3 py-1.5 rounded-lg border border-red-300 bg-red-100 text-red-900 font-semibold hover:bg-red-200"
                          >
                            Debug only mode
                          </button>
                        </div>
                      )}
                    </div>

                    {process.env.NODE_ENV === 'development' && (
                      <div className="space-y-2 text-xs font-mono">
                        <div>
                          <span className="font-semibold">Health:</span>{' '}
                          {apiHealth.status ? `${apiHealth.ok ? 'OK' : `Failed (${apiHealth.status})`}` : 'Unknown'}
                        </div>
                        {apiHealth.error && (
                          <div className="text-red-800"><span className="font-semibold">Health error:</span> {apiHealth.error}</div>
                        )}
                        {apiHealth.body && (
                          <pre className="max-h-36 overflow-auto rounded border border-red-200 bg-white p-3 text-xs text-slate-900">{JSON.stringify(apiHealth.body, null, 2)}</pre>
                        )}
                      </div>
                    )}

                    {process.env.NODE_ENV === 'development' && registrationDebug.requestPayload && (
                      <div className="mt-4 rounded border border-red-200 bg-white p-3">
                        <div className="font-semibold text-sm text-red-900 mb-2">Last registration request</div>
                        <pre className="max-h-48 overflow-auto text-xs text-slate-900">{JSON.stringify(registrationDebug.requestPayload, null, 2)}</pre>
                      </div>
                    )}

                    {registrationDebug.responseBody && (
                      <div className="mt-4 rounded border border-red-200 bg-white p-3">
                        <div className="font-semibold text-sm text-red-900 mb-2">Last registration response</div>
                        <pre className="max-h-48 overflow-auto text-xs text-slate-900">{JSON.stringify({ status: registrationDebug.responseStatus, ok: registrationDebug.responseOk, body: registrationDebug.responseBody }, null, 2)}</pre>
                      </div>
                    )}

                    {registrationDebug.errorMessage && (
                      <div className="mt-4 rounded border border-red-300 bg-red-100 p-3 text-sm font-semibold text-red-900">
                        Error: {registrationDebug.errorMessage}
                      </div>
                    )}
                  </div>
                  */}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No registration data found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* DEBUG OVERLAY HIDDEN
      {process.env.NODE_ENV === 'development' && showDebugOnly && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black/40 p-6">
          <div className="mx-auto max-w-5xl rounded-3xl border border-red-200 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4 pb-4 border-b border-red-100 mb-4">
              <div>
                <h2 className="text-xl font-semibold text-red-900">Registration API Debug Only</h2>
                <p className="text-sm text-red-700">Use this overlay to isolate the /api/register request and response.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowDebugOnly(false)}
                className="rounded-lg border border-red-300 bg-red-100 px-4 py-2 text-sm font-semibold text-red-900 hover:bg-red-200"
              >
                Close
              </button>
            </div>
            <div className="space-y-4">
              <div className="space-y-2 text-xs font-mono text-slate-900">
                <div>
                  <span className="font-semibold">Health:</span>{' '}
                  {apiHealth.status ? `${apiHealth.ok ? 'OK' : `Failed (${apiHealth.status})`}` : 'Unknown'}
                </div>
                {apiHealth.error && (
                  <div className="text-red-800"><span className="font-semibold">Health error:</span> {apiHealth.error}</div>
                )}
                {apiHealth.body && (
                  <pre className="max-h-40 overflow-auto rounded border border-red-200 bg-slate-50 p-3">{JSON.stringify(apiHealth.body, null, 2)}</pre>
                )}
              </div>

              {registrationDebug.requestPayload && (
                <div className="rounded border border-red-200 bg-slate-50 p-3">
                  <div className="font-semibold text-sm text-red-900 mb-2">Last registration request</div>
                  <pre className="max-h-64 overflow-auto text-xs">{JSON.stringify(registrationDebug.requestPayload, null, 2)}</pre>
                </div>
              )}

              {registrationDebug.responseBody && (
                <div className="rounded border border-red-200 bg-slate-50 p-3">
                  <div className="font-semibold text-sm text-red-900 mb-2">Last registration response</div>
                  <pre className="max-h-64 overflow-auto text-xs">{JSON.stringify({ status: registrationDebug.responseStatus, ok: registrationDebug.responseOk, body: registrationDebug.responseBody }, null, 2)}</pre>
                </div>
              )}

              {registrationDebug.errorMessage && (
                <div className="rounded border border-red-300 bg-red-100 p-3 text-sm font-semibold text-red-900">
                  Error: {registrationDebug.errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      */}

      <Footer />
    </div>
  );
};

export default BillingPage;